import { useCallback, useContext, useEffect, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import type {
  CellEditRequestEvent,
  ColDef,
  GetDetailRowDataParams,
  IRowNode,
  ModelUpdatedEvent,
  RowClassRules,
  RowNode,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { addHours } from 'date-fns';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { SelectItemOptionsType } from 'primereact/selectitem';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { addToast } from '@/app/slices/toast';
import { WithLocalStorage } from '@/components';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { RowIdParams } from '@/grid/types';
import { PrimaryContext } from '@/services';
import { useAccessToken } from '@/services/auth';
import {
  useFertScheduledDonorsLazy,
  useInlineFertCommentsQuery,
} from '@/services/fertilization/hooks';
import { semenSortersOptions } from '@/services/semen-sorters';
import { useContextMenu } from '@/utils/context-menu';

import { Caprine, Ovine } from './donorBreeds';
import { FertilizationDonorToolbar } from './FertilizationDonorToolbar';
import { DonorAspiration } from './grid-cell-rendered-components/DonorAspiration';
import { DonorName } from './grid-cell-rendered-components/DonorName';
import { DonorSemen } from './grid-cell-rendered-components/DonorSemen';
import { PulledSemenName } from './grid-cell-rendered-components/PulledSemenName';
import { ScheduledDonorName } from './grid-cell-rendered-components/ScheduledDonorName';
import { timeZones } from './timeZones';
import { FertilizationDonorScheduleType, PulledSemenType } from './types';
import { useFertilizationContext } from './useFertilizationContext';

import './Fertilization.css';

function MofloDropdown({
  onChange,
  value,
  siteId,
}: {
  siteId: string | null;
  value: string | null;
  onChange(value: string): void;
}) {
  const token = useAccessToken();
  const { data } = useQuery({
    ...semenSortersOptions(token, {
      siteId: siteId!,
      take: 100,
    }),
    enabled: !!token && !!siteId,
  });
  const mofloNums = data?.items.map((sorter) => ({
    value: sorter.name,
    text: sorter.name,
  }));

  return (
    <Dropdown
      className="moflo-dropdown"
      options={mofloNums}
      optionLabel="text"
      onChange={(event) => onChange(event.value)}
      value={value}
      showClear
    />
  );
}

type Props = {
  className?: string;
};
export const FertilizationPanel = ({ className }: Props) => {
  const defaultGridOptions = useDefaultGridOptions();
  const { t } = useTranslation();
  const { themeMode } = useContext(PrimaryContext);
  const [params] = useSearchParams();
  const fertilizationDate = params.get('date');
  const fertilizationSite = params.get('site');
  const {
    pulledSemenData,
    fertilizationDonorScheduleData,
    donorGridRef,
    semenGridRef,
    setSelectedSemenRows,
    handleSelectionChanged,
    updateFilters,
    poolSires,
  } = useFertilizationContext();
  const inlineFertCommentsRes = useInlineFertCommentsQuery();
  const fetchScheduledDonors = useFertScheduledDonorsLazy();
  const inlineFertCommentsOptions = inlineFertCommentsRes.data;
  const dispatch = useDispatch();
  const pulledSemenGridData = useMemo(() => {
    if (!pulledSemenData) return undefined;

    const copy = JSON.parse(
      JSON.stringify(pulledSemenData),
    ) as PulledSemenType[];

    return copy.map((item: PulledSemenType, _i, list) => {
      const semenCount = list.filter(
        (row: PulledSemenType) => row.semenID === item.semenID,
      ).length;
      const sireCount = list.filter(
        (row: PulledSemenType) => row.sireID === item.sireID,
      ).length;
      return {
        ...item,
        hasMultipleSemen: semenCount > 1,
        hasMultipleSires: sireCount > 1,
      };
    });
  }, [pulledSemenData]);

  const detailCellRendererParams = useMemo(() => {
    return {
      detailGridOptions: {
        autoSizeColumns: true,
        suppressRowVirtualisation: true,
        columnDefs: [
          {
            field: 'donorName',
            cellRenderer: ScheduledDonorName,
            headerName: 'Donor',
          },
          { field: 'csr', headerName: 'CSR' },
        ],
      },
      getDetailRowData: async (params: GetDetailRowDataParams) => {
        const result = await fetchScheduledDonors({
          semenID: params.data.semenID,
          donorScheduleDate: fertilizationDate as string,
          semenType: params.data.semenTypeName,
          accountID: params.data.accountID,
        });

        params.successCallback(result);
      },
    };
  }, [fertilizationDate, fetchScheduledDonors]);

  const handleMoflo = useCallback(
    (value: string, params: { node: RowNode }) => {
      const newData = {
        ...params.node.data,
      } as PulledSemenType;
      newData.moflo = value;
      const tx = {
        update: [newData],
      };
      semenGridRef.current?.api.applyTransaction(tx);
      setSelectedSemenRows(
        semenGridRef.current?.api.getSelectedRows() as PulledSemenType[],
      );
    },
    [semenGridRef, setSelectedSemenRows],
  );
  const pulledSemenColDefs = useMemo<ColDef<PulledSemenType>[]>(
    () => [
      {
        cellRenderer: 'agGroupCellRenderer',
        width: 5,
        cellClassRules: {
          'semen-match': (params) => !!params.data?.hasMultipleSemen,
          'sire-match': () => {
            return false;
          },
          'zero-pulled': () => {
            return false;
          },
          'inline-edit': () => {
            return false;
          },
        },
      },
      {
        field: 'name_Type',
        headerName: 'Semen (Type)',
        cellRenderer: PulledSemenName,
        width: 100,
        wrapText: true,
        autoHeight: true,
        cellClassRules: {
          'semen-match': () => {
            return false;
          },
          'sire-match': (params) => !!params.data?.hasMultipleSires,
          'zero-pulled': () => {
            return false;
          },
          'inline-edit': () => {
            return false;
          },
        },
      },
      {
        field: 'semenName',
        hide: true,
        filter: true,
        filterParams: { readOnly: true },
      },
      {
        field: 'semenTypeName',
        hide: true,
        filter: true,
        filterParams: { readOnly: true },
      },
      {
        field: 'moflo',
        headerName: 'Moflo #',
        width: 105,
        cellRenderer: (params: {
          data: { moflo: string | null; semenTypeName: string };
          node: RowNode;
        }) => {
          if (['RS F', 'RS M'].includes(params.data.semenTypeName)) {
            return (
              <MofloDropdown
                siteId={fertilizationSite}
                onChange={(value) => handleMoflo(value, params)}
                value={params.data.moflo}
              />
            );
          }
        },
      },
      { field: 'sireBreed', headerName: 'Breed', width: 80 },
      { field: 'accountName', headerName: 'Ranch', width: 80 },
      {
        field: 'semenStorageName',
        headerName: 'Location',
        width: 100,
        hide: true,
      },
      {
        field: 'cane',
        headerName: 'Cane Code',
        hide: true,
        width: 120,
        wrapText: true,
        autoHeight: true,
      },
      { field: 'colCode', width: 80, wrapText: true, autoHeight: true },
      { field: 'naab', headerName: 'NAAB', width: 80 },
      {
        field: 'pulledQuantity',
        headerName: 'Pulled',
        width: 80,
        cellClassRules: {
          'semen-match': () => false,
          'sire-match': () => false,
          'zero-pulled': (params) => {
            return (params.data?.pulledQuantity ?? 0) < 1;
          },
          'inline-edit': () => {
            return false;
          },
        },
      },
      {
        field: 'use',
        editable: true,
        width: 80,
        cellClassRules: {
          'semen-match': () => false,
          'sire-match': () => false,
          'zero-pulled': () => false,
          'inline-edit': () => {
            return true;
          },
        },
      },
    ],
    [handleMoflo, fertilizationSite],
  );

  const semenRowClassRules = useMemo(
    () => ({
      'zero-sires': (params: { data: { donors_Sire: number } }) =>
        params.data.donors_Sire === 0,
    }),
    [],
  );

  const changeToLocalTime = useCallback(
    (date: Date, matTime: string) => {
      const site = params.get('site');
      const siteTimeZone = timeZones[site as string];
      const formattedTime = matTime.padStart(4, '0');
      const hours = parseInt(formattedTime.slice(0, 2), 10);
      const minutes = parseInt(formattedTime.slice(2), 10);
      date.setHours(hours);
      date.setMinutes(minutes);
      const siteCurrentTime = new Date(
        date.toLocaleString('en-US', {
          timeZone: siteTimeZone,
        }),
      );
      const diff = siteCurrentTime.getTime() - date.getTime();
      return new Date(date.getTime() - diff);
    },
    [params],
  );

  const isInWindow = useCallback(
    (localAspDateTime: Date, earlist: number, latest: number) => {
      const earliestTime = addHours(localAspDateTime, earlist);
      const latestTime = addHours(localAspDateTime, latest);
      const now = new Date();
      const window = earliestTime < now && now < latestTime;
      return window;
    },
    [],
  );

  const checkWindow = useCallback(
    (
      aspDate: string,
      matTime: string,
      breedCode: string,
      breedName: string,
    ) => {
      const onsiteDate = new Date(aspDate);
      const convertedTime = changeToLocalTime(onsiteDate, matTime);

      let earliest = 21;
      let latest = 24;

      if (Caprine[breedCode] === breedName || Ovine[breedCode] === breedName) {
        earliest = 24;
        latest = 26;
      }

      return isInWindow(convertedTime, earliest, latest);
    },
    [changeToLocalTime, isInWindow],
  );

  useEffect(() => {
    const timeout = setInterval(() => {
      donorGridRef.current?.api.redrawRows();
    }, 60000);
    return () => clearInterval(timeout);
  }, [donorGridRef]);

  const donorRowClassRules = useMemo(
    () => ({
      'outside-fert-window': (params: {
        data: {
          aspDate: string;
          matTime: string;
          donorBreed: string;
          donorBreedName: string;
        };
      }) => {
        return !checkWindow(
          params.data.aspDate,
          params.data.matTime,
          params.data.donorBreed,
          params.data.donorBreedName,
        );
      },
    }),
    [checkWindow],
  );
  const handleInlineFertComments = useCallback(
    (e: { value: number[] }, params: { node: RowNode }) => {
      const newData = {
        ...params.node.data,
      } as FertilizationDonorScheduleType;
      newData.comments = e.value;
      const tx = {
        update: [newData],
      };
      donorGridRef.current?.api.applyTransaction(tx);
    },
    [donorGridRef],
  );
  const handleContamination = useCallback(
    (isContaminated: boolean, params: { node: RowNode }) => {
      const newData = {
        ...params.node.data,
      } as FertilizationDonorScheduleType;
      newData.contamination = isContaminated;
      const tx = {
        update: [newData],
      };
      donorGridRef.current?.api.applyTransaction(tx);
    },
    [donorGridRef],
  );
  const CheckboxRenderer = useMemo(
    () => (params: { value: boolean | undefined; node: RowNode }) => {
      const value = params.value ?? false;
      return (
        <input
          type="checkbox"
          checked={value}
          readOnly
          onClick={() => handleContamination(!value, params)}
        />
      );
    },
    [handleContamination],
  );
  const donorColDef = useMemo(
    () =>
      ({
        ...defaultColDef,
        headerCheckboxSelectionFilteredOnly: true,
      }) as ColDef<FertilizationDonorScheduleType>,
    [],
  );
  const fertDonorColDefs: ColDef<FertilizationDonorScheduleType>[] = useMemo(
    () => [
      {
        field: 'matTime',
        headerCheckboxSelection: true,
        checkboxSelection: true,
        suppressMovable: true,
        lockPosition: true,
        width: 90,
      },

      {
        field: 'ivmMat',
        cellRenderer: DonorAspiration,
        headerName: '# in Mat',
        width: 70,
      },
      {
        field: 'donorName',
        cellRenderer: DonorName,
        width: 100,
        wrapText: true,
        autoHeight: true,
      },
      { field: 'donorBreed', width: 85 },
      {
        field: 'name_Type',
        cellRenderer: DonorSemen,
        headerName: 'Semen (Type)',
        width: 100,
        wrapText: true,
        autoHeight: true,
        filter: true,
        filterParams: { readOnly: true },
      },
      {
        field: 'semenTypeName',
        hide: true,
        filter: true,
        filterParams: { readOnly: true },
      },
      {
        field: 'numFerts',
        headerName: '# in Fert',
        width: 70,
        editable: true,
        cellClassRules: {
          'inline-edit': () => {
            return true;
          },
        },
      },
      {
        field: 'time',
        width: 70,
        wrapText: true,
        editable: true,
        cellClassRules: {
          'inline-edit': () => {
            return true;
          },
        },
      },
      {
        field: 'contamination',
        cellDataType: 'boolean',
        cellRenderer: CheckboxRenderer,
        headerName: 'Contamination Present',
        width: 70,
      },
      { field: 'accountNumber', headerName: 'RanchID', width: 100 },
      { field: 'semenComment', width: 110, wrapText: true, autoHeight: true },
      { field: 'exportName', headerName: 'Export', width: 85 },
      {
        field: 'fertComment',
        editable: true,
        width: 100,
        wrapText: true,
        autoHeight: true,
        cellClassRules: {
          'inline-edit': () => {
            return true;
          },
        },
      },
      {
        field: 'comments',
        width: 135,
        cellRenderer: (params: {
          data: { comments: SelectItemOptionsType };
          node: RowNode;
        }) => {
          return (
            <MultiSelect
              options={inlineFertCommentsOptions as SelectItemOptionsType}
              optionLabel="text"
              onChange={(event) => handleInlineFertComments(event, params)}
              value={params.data.comments}
              maxSelectedLabels={1}
            />
          );
        },
      },
    ],
    [CheckboxRenderer, handleInlineFertComments, inlineFertCommentsOptions],
  );
  const handleUseEditRequest = useCallback(
    (event: CellEditRequestEvent) => {
      const oldData = event.data;
      const field = event.colDef.field;
      if (!field) {
        return;
      }

      if (isNaN(parseInt(event.newValue)) && event.newValue !== '') {
        return;
      }
      if (oldData.pulledQuantity < event.newValue) {
        return;
      }

      const newData = { ...oldData };
      newData[field] = event.newValue;

      const tx = {
        update: [newData],
      };

      event.api.applyTransaction(tx);
      if (event.newValue === '0' || event.newValue === '') {
        event.node.setSelected(false);
      } else {
        event.node.setSelected(true);
      }
      setSelectedSemenRows(
        semenGridRef.current?.api.getSelectedRows() as PulledSemenType[],
      );
      updateFilters(poolSires);
    },
    [poolSires, semenGridRef, setSelectedSemenRows, updateFilters],
  );

  const validateTime = useCallback(
    (lookup: string) => {
      if (lookup != null && lookup.length !== 0) {
        if (lookup.indexOf(':') > 0) {
          lookup = lookup.replace(':', '');
        }
        const regex = new RegExp(/^([01]\d|2[0-3])([0-5]\d)$/);
        if (regex.test(lookup) !== true) {
          dispatch(
            addToast({
              severity: 'error',
              summary: t('ivf.invalidIvfTime'),
              detail: t('ivf.invalidIvfTimeValue'),
              life: ToastLife.DEFAULT,
            }),
          );
          return false;
        }
        return true;
      }
    },
    [dispatch],
  );

  const handleCellEditRequest = useCallback(
    (event: CellEditRequestEvent) => {
      const oldData = event.data;
      const field = event.colDef.field;

      if (!field) {
        return;
      }
      if (field === 'numFerts') {
        if (isNaN(parseInt(event.newValue)) && event.newValue !== '') {
          return;
        }
      }
      if (field === 'time') {
        if (validateTime(event.newValue) === false) {
          return;
        }
      }
      const newData = { ...oldData };
      newData[field] = event.newValue;
      const tx = {
        update: [newData],
      };
      event.api.applyTransaction(tx);
      const node: IRowNode[] = [];
      node.push(
        donorGridRef.current?.api.getRowNode(
          newData.donorScheduleID as string,
        ) as IRowNode<FertilizationDonorScheduleType>,
      );
      donorGridRef.current?.api.setNodesSelected({
        nodes: node,
        newValue: true,
      });
      handleSelectionChanged();
    },
    [donorGridRef, handleSelectionChanged, validateTime],
  );

  const getSemenRowId = useCallback(
    (params: RowIdParams<PulledSemenType>) => params.data.rowNum.toString(),
    [],
  );
  const getDonorRowId = useCallback(
    (params: RowIdParams<FertilizationDonorScheduleType>) =>
      params.data.donorScheduleID,
    [],
  );

  const { getContextMenuItems: semenContextMenu } = useContextMenu({
    idKey: 'semenID',
  });
  const { getContextMenuItems: donorContextMenu } = useContextMenu({
    idKey: 'donorScheduleID',
  });

  const onModelUpdated = useCallback((params: ModelUpdatedEvent) => {
    params.api?.redrawRows();
  }, []);
  return (
    <Splitter className={`fertilization__splitter ${className}`.trim()}>
      <SplitterPanel size={38}>
        <WithLocalStorage storageKey="fertilization-pulled-semen-grid">
          <AgGridReact
            gridOptions={defaultGridOptions}
            ref={semenGridRef}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            masterDetail={true}
            detailCellRendererParams={detailCellRendererParams}
            className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} dark-borders`}
            defaultColDef={defaultColDef}
            columnDefs={pulledSemenColDefs}
            overlayLoadingTemplate={t(
              'fertilization.pleaseSelectDateAndFertilizationSite',
            )}
            rowData={pulledSemenGridData}
            onCellEditRequest={handleUseEditRequest}
            readOnlyEdit={true}
            onModelUpdated={onModelUpdated}
            getRowId={getSemenRowId}
            suppressRowVirtualisation={true}
            rowClassRules={semenRowClassRules as RowClassRules<PulledSemenType>}
            getContextMenuItems={semenContextMenu}
          />
        </WithLocalStorage>
      </SplitterPanel>
      <SplitterPanel size={62}>
        <div className="donor-grid-wrapper">
          <FertilizationDonorToolbar />
          <WithLocalStorage storageKey="fertilization-donor-grid">
            <AgGridReact
              gridOptions={defaultGridOptions}
              ref={donorGridRef}
              className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} compact-inputs donor-toolbar-shadow dark-borders`}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              defaultColDef={donorColDef}
              columnDefs={fertDonorColDefs}
              overlayLoadingTemplate={t(
                'fertilization.pleaseSelectDateAndFertilizationSite',
              )}
              onCellEditRequest={handleCellEditRequest}
              rowData={fertilizationDonorScheduleData}
              onSelectionChanged={handleSelectionChanged}
              readOnlyEdit={true}
              getRowId={getDonorRowId}
              suppressRowVirtualisation={true}
              rowClassRules={
                donorRowClassRules as RowClassRules<FertilizationDonorScheduleType>
              }
              getContextMenuItems={donorContextMenu}
            />
          </WithLocalStorage>
        </div>
      </SplitterPanel>
    </Splitter>
  );
};
